import React, { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import config from '../Config'
import { Swiper as SwiperType } from 'swiper'
import { Mousewheel, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import SwiperCore from 'swiper'
import Animation4 from '../animations/animation4'

gsap.registerPlugin(ScrollTrigger)
SwiperCore.use([Mousewheel, Pagination])

export const Aboutcontent = () => {
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0.5)
  const totalSlides = 11
  const slidesPerView = 3

  const swiperRef = useRef<SwiperType | null>(null)
  const sectionRef = useRef<HTMLDivElement | null>(null)

  // Track progress bar updates (if you re-enable it later)
  useEffect(() => {
    const handleProgressUpdate = () => {
      if (swiperRef.current) {
        const currentIndex = swiperRef.current.activeIndex
        const calculatedProgress =
          currentIndex / (totalSlides - slidesPerView)
        setProgress(Math.min(0.5 + calculatedProgress * 0.5, 1))
      }
    }

    if (swiperRef.current) {
      swiperRef.current.on('slideChange', handleProgressUpdate)
      handleProgressUpdate()
    }

    return () => {
      if (swiperRef.current) {
        swiperRef.current.off('slideChange', handleProgressUpdate)
      }
    }
  }, [])

  // GSAP horizontal scroll effect
  useEffect(() => {
    if (typeof window === 'undefined') return

    const section = sectionRef.current
    if (!section) return

    // ✅ Target outer wrapper instead of Swiper’s internal .swiper-wrapper
    const wrapper = section.querySelector(
      '.swiper-anim-wrapper'
    ) as HTMLElement
    if (!wrapper) return

    let ctx: gsap.Context

    const buildAnimation = () => {
      if (ctx) ctx.revert()

      ctx = gsap.context(() => {
        const containerWidth = section.clientWidth
        const totalWidth = wrapper.scrollWidth

        const moveX = Math.max(totalWidth - containerWidth, 0)

        if (moveX > 0) {
          gsap.to(wrapper, {
            x: -moveX,
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              start: 'top top',
              end: () => `+=${section.offsetHeight + moveX}`,
              scrub: true,
              pin: false,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          })
        }
      }, section)
    }

    buildAnimation()

    const handleResize = () => {
      ScrollTrigger.getAll().forEach(st => st.kill())
      buildAnimation()
      ScrollTrigger.refresh()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (ctx) ctx.revert()
      ScrollTrigger.getAll().forEach(st => st.kill())
    }
  }, [])

  // Force refresh after Swiper init
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.on('init', () => {
        setTimeout(() => {
          swiperRef.current?.update()
          ScrollTrigger.refresh()
        }, 300)
      })
    }

    setTimeout(() => ScrollTrigger.refresh(), 1000)
  }, [])

  

  return (
    <>
      <section className="abut-area gradient-background5">
        <div className="wave3 d-lg-block">
          <img
            src={`${config.siteBaseUrl}/assets/imgs/wave-12.png`}
            alt=""
          />
        </div>

        <div className="container">
          <div className="row justify-content-md-center">
            <div className="col-lg-8 mb-40 text-center">
              <h6 className="seamless-heading abut-txt">Our mission</h6>
              <p className="abut-txt1">
                At TCB Pay, we pride ourselves on being transparent with
                our clients and gladly choose long-term relationships over
                profit. As an independent global processing payment
                solution, we set ourselves apart from the competition with
                our thorough customer service team and our dedicated team
                members.
              </p>
            </div>
          </div>
        </div>

        <div className="wave4 d-lg-block">
          <img
            src={`${config.siteBaseUrl}/assets/imgs/wave-13.png`}
            alt=""
          />
        </div>
      </section>

      {/* Horizontal Swiper + GSAP */}
      <section className="abut-seclast">
        <img
          src={`${config.siteBaseUrl}/assets/imgs/blue-lowerdots.svg`}
          alt="Dots"
          className="orng-dots"
        />
        <div className="container slider-swiper1 d-none">
          <div className="row">
            <div className="col-md-12">
              <h6 className="seamless-heading">Our story</h6>
            </div>
          </div>
        </div>

        <div className="slider-swiper1 d-none" ref={sectionRef}>
          <div className="swiper-anim-wrapper">
            <Swiper
                  slidesPerView={4}
                  spaceBetween={0}
                  pagination={{ clickable: true }}
                  mousewheel={true}
                  direction={'horizontal'}
                  modules={[Mousewheel, Pagination]}
                  onSwiper={(swiper: SwiperType) => {
                    swiperRef.current = swiper
                  }}
                  breakpoints={{
                    320: { slidesPerView: 1 },
                    576: { slidesPerView: 2 },
                    768: { slidesPerView: 2 },
                    1200: { slidesPerView: 4 },
                    1700: { slidesPerView: 5 },
                    1900: { slidesPerView: 5 },
                    2200: { slidesPerView: 5 },
                  }}
                  className="mySwiper"
            >
              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="bluebx">
                    <h4>2014</h4>
                    <p>
                      TCB Pay was founded in <b>Henderson, NV.</b>
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line1.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="yelwbx">
                    <h4>2015</h4>
                    <p>
                      Expanded and opened a satellite office in{' '}
                      <b>Los Angeles, CA.</b>
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line2.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="bluebx">
                    <h4>2017</h4>
                    <p>
                      Began hiring an in-house development team to{' '}
                      <b>build proprietary software/tools.</b>
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line3.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="yelwbx">
                    <h4>2018</h4>
                    <p>
                      Opened a satellite office in{' '}
                      <b>Toronto, Canada</b>.
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line4.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="bluebx">
                    <h4>2019</h4>
                    <p>
                      The first <b>TCB Pay Gateway</b> was created.
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line5.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="yelwbx">
                    <h4>2020</h4>
                    <p>
                      The <b>TCB Pay Web Application</b> and the{' '}
                      <b>TCB Pay App</b> were released.
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line6.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="bluebx">
                    <h4>2021</h4>
                    <p>
                      Achieved <b>PCI Level 1 Compliance</b> status
                      certified by WorldPay.
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line7.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="yelwbx">
                    <h4>2022</h4>
                    <p>
                      Became certified with <b>TSYS</b>. Moved
                      headquarters to <b>Boca Raton, FL.</b>
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line8.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="bluebx">
                    <h4>2023</h4>
                    <p>
                      <b>TCB Pay Issuing</b> was released.
                      <br />
                      Opened a satellite office in <b>Malta. </b>
                    </p>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line9.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="yelwbx">
                    <h4>2024</h4>
                    <p>
                      Launch of the <b>TCB Pay Portal,</b> an upgraded
                      version of the TCB Pay Gateway featuring significant
                      enhancements and additional functionalities.
                    </p>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="slider-swiperbx1">
                  <div className="abut-linecruve">
                    <img
                      src={`${config.siteBaseUrl}/assets/imgs/about/abut-line10.png`}
                      alt="Line"
                    />
                  </div>
                  <div className="bluebx">
                    <h4>2025</h4>
                    <p>
                      <b>TCB Pay Portal App</b>launch.
                      Made the entire TCB Pay platform <br/> multilingual, now<br/> 
                      supporting <b>French</b> and <b>Spanish.</b>
                    </p>
                  </div>
                </div>
              </SwiperSlide>

            </Swiper>
          </div>
        </div>

        <div className="animation4">
           <Animation4 />
        </div>
        
      </section>

      

      {/* Culture */}
      <section className="abut-area1 gradient-background6">
        <div className="wave3 d-lg-block">
          <img
            src={`${config.siteBaseUrl}/assets/imgs/wave-14.png`}
            alt="Graphic waves"
          />
        </div>

        <div className="container">
          <div className="row justify-content-md-center">
            <div className="col-lg-8 mb-40 text-center">
              <h6 className="seamless-heading abut-txt2">
                Our culture
              </h6>
              <p className="abut-txt3">
                At TCB Pay, we think the only way to do business is to
                serve others, do more for them, and charge less. We have
                shaped our company and everything we do by this motto
                because it’s true to the values of those we do business
                with. <br />
                We are here to support independent business owners and
                take one less worry off their back, whether it is
                accepting payments, consulting with them on business
                solutions, or just providing customer service.
              </p>
            </div>
          </div>
        </div>

        <div className="wave4 d-lg-block">
          <img
            src={`${config.siteBaseUrl}/assets/imgs/wave-15.png`}
            alt="Graphic waves"
          />
        </div>
      </section>

      {/* Values */}
      <section className="abut-seclast pb-210">
        <img
          src={`${config.siteBaseUrl}/assets/imgs/blue-lowerdots.svg`}
          alt="Dots"
          className="orng-dots"
        />
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <h6 className="seamless-heading mb-90">Our values</h6>
            </div>
            <div className="col-md-6">
              <div className="abut-seclast-inner">
                <div className="abut-iconbx">
                  <img
                    src={`${config.siteBaseUrl}/assets/imgs/abut-icon1.svg`}
                    alt="Line"
                  />
                </div>
                <div className="abut-iconbx1">
                  <div className="abut-iconbxtxt">Teamwork</div>
                  <div className="abut-iconbxtxt1">
                    Together, we achieve more, leveraging diverse
                    perspectives and collaborative efforts to drive
                    success.
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="abut-seclast-inner">
                <div className="abut-iconbx">
                  <img
                    src={`${config.siteBaseUrl}/assets/imgs/abut-icon2.svg`}
                    alt=""
                  />
                </div>
                <div className="abut-iconbx1">
                  <div className="abut-iconbxtxt">Think outside the box</div>
                  <div className="abut-iconbxtxt1">
                    We embrace creativity and innovation, constantly
                    seeking new perspectives and solutions to overcome
                    challenges.
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="abut-seclast-inner">
                <div className="abut-iconbx">
                  <img
                    src={`${config.siteBaseUrl}/assets/imgs/abut-icon3.svg`}
                    alt="Respect and inclusion"
                  />
                </div>
                <div className="abut-iconbx1">
                  <div className="abut-iconbxtxt">
                    Respect and inclusion
                  </div>
                  <div className="abut-iconbxtxt1">
                    We value and celebrate the unique contributions of
                    every individual, fostering an environment of respect,
                    dignity, and belonging.
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="abut-seclast-inner">
                <div className="abut-iconbx">
                  <img
                    src={`${config.siteBaseUrl}/assets/imgs/abut-icon4.svg`}
                    alt="Growth"
                  />
                </div>
                <div className="abut-iconbx1">
                  <div className="abut-iconbxtxt">Growth</div>
                  <div className="abut-iconbxtxt1">
                    Our commitment to continuous improvement and personal
                    development empowers each team member to reach their
                    full potential.
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="abut-seclast-inner">
                <div className="abut-iconbx">
                  <img
                    src={`${config.siteBaseUrl}/assets/imgs/abut-icon5.svg`}
                    alt="Trust"
                  />
                </div>
                <div className="abut-iconbx1">
                  <div className="abut-iconbxtxt">Trust</div>
                  <div className="abut-iconbxtxt1">
                    We prioritize integrity and transparency in all our
                    interactions, fostering trust among our team members,
                    partners, and merchants.
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="abut-seclast-inner">
                <div className="abut-iconbx">
                  <img
                    src={`${config.siteBaseUrl}/assets/imgs/abut-icon6.svg`}
                    alt="Work hard, play hard"
                  />
                </div>
                <div className="abut-iconbx1">
                  <div className="abut-iconbxtxt">Work hard, play hard</div>
                  <div className="abut-iconbxtxt1">
                    We maintain a balance between dedication to our goals
                    and enjoying the journey, celebrating achievements,
                    and fostering a supportive culture.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
